import { type Component, markRaw } from 'vue'

import type {
  PersonalizedFeedApiProps,
  PersonalizedFeedItem,
} from '@backmarket/http-api/src/api-specs-content/models/personalized-feed'
import type { ProductQuery } from '@backmarket/http-api/src/api-specs-content/models/product-query'
import RecommendationAlgoliaCarousel, {
  type RecommendationAlgoliaCarouselProps,
} from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import type { SortAlgoliaHitsFn } from '../../products/models/sort-alglia-hits-fn'
import type { BlockRendererProps } from '../../shared-components/BlockRenderer/BlockRenderer.types'
import BlockRenderer from '../../shared-components/BlockRenderer/BlockRenderer.vue'
import RefinedProducts, {
  type RefinedProductsBlockProps,
} from '../RefinedProducts/RefinedProducts.vue'

export type BrazeContentCardExtras = {
  feed?: string
  type?: string
  title?: string
  product_granularity?: 'variant' | 'backbox' | 'parent'
  product_id?: string
  filters?: string
  priority?: string
  icon?: string
  recommendation_type?: string
  content_card_family?: string
  sale_end_date?: string
} & Record<string, string>

export type BrazeContentCard = {
  expiresAt: Date | null
  id?: string
  extras: BrazeContentCardExtras
  isControl: boolean
  pinned: boolean
  updated: Date | null
  viewed: boolean
}

// Define supported ContentCard types in one place for easy extension
// When adding a new type, add it here and add a corresponding case in mapPropsByBlockType
export type SupportedContentCardType =
  | RecommendationAlgoliaCarouselProps
  | RefinedProductsBlockProps
export type SupportedFallbackTypes = BlockRendererProps

export type ContentCard = {
  id: string
  title: string
  icon?: string
  tag: Component
  props: SupportedContentCardType | SupportedFallbackTypes
  productIds: string[]
  saleEndDate?: string
  priority?: number
  source: 'content-card' | 'fallback' | 'additionalRecommendation'
}

function mapPropsByBlockType({
  type,
  brazeContentCardExtras,
  productQuery,
  blockPosition,
  index,
}: {
  type: string
  brazeContentCardExtras: BrazeContentCardExtras
  productQuery: PersonalizedFeedApiProps['productQuery']
  blockPosition: number
  index: number
}):
  | { title: string; tag: Component; props: SupportedContentCardType }
  | undefined {
  const logger = useLogger()

  if (!brazeContentCardExtras.title) {
    logger.error(
      `[PERSONALIZED_CONTENT_CARD] Missing title for content card type: ${type}`,
      {
        owners: ['bot-squad-content-front'],
      },
    )

    return undefined
  }

  switch (type) {
    case 'recommendations':
      return buildRecommendationAlgoliaCarouselProps(
        brazeContentCardExtras,
        logger,
        { blockPosition, index },
      )

    case 'search':
      return buildRefinedProductsProps(brazeContentCardExtras, productQuery, {
        blockPosition,
        index,
      })

    default:
      logger.error(
        `[PERSONALIZED_CONTENT_CARD] Unsupported content card type: ${type}`,
        {
          owners: ['bot-squad-content-front'],
        },
      )

      return undefined
  }
}
// objectID is the id of a backbox product (syntax: <product-id>_<grade>_<special-offer-type>)
// id is the id of a product ("variant")
function getIdFacetFromGranularity(
  granularity: 'variant' | 'backbox' | 'parent' | string,
): 'id' | 'objectID' {
  return ['variant', 'parent'].includes(granularity) ? 'id' : 'objectID'
}
function buildIdFacetFilters(brazeContentCard: BrazeContentCardExtras) {
  if (!brazeContentCard.product_id) {
    return undefined
  }

  const idFacet = getIdFacetFromGranularity(
    brazeContentCard.product_granularity || '',
  )

  const filters = brazeContentCard.product_id
    ?.trim()
    ?.split(',')
    ?.map((id) => `${idFacet}:${id}`)
    ?.join(' OR ')

  return filters ? `(${filters})` : undefined
}

function buildFilters(brazeContentCard: BrazeContentCardExtras) {
  const filters = buildIdFacetFilters(brazeContentCard)

  if (!filters && brazeContentCard.filters) {
    return brazeContentCard.filters
  }

  if (brazeContentCard.filters) {
    return `${filters} AND ${brazeContentCard.filters}`
  }

  return filters
}

function mergeAPIAndBrazeProductQuery(
  brazeContentCard: BrazeContentCardExtras,
  apiProductQueryByGranularity: PersonalizedFeedApiProps['productQuery'],
): ProductQuery {
  const productQuery =
    apiProductQueryByGranularity[
      brazeContentCard.product_granularity as keyof typeof apiProductQueryByGranularity
    ]

  if (!productQuery) {
    return {
      apiKey: '',
      indexName: '',
      params: {},
    }
  }

  return {
    ...productQuery,
    params: {
      ...productQuery.params,
      filters: buildFilters(brazeContentCard),
    },
  }
}
function buildRefinedProductsProps(
  brazeContentCardExtras: BrazeContentCardExtras,
  productQuery: PersonalizedFeedApiProps['productQuery'],
  { blockPosition, index }: { blockPosition: number; index: number },
) {
  const finalProductQuery = mergeAPIAndBrazeProductQuery(
    brazeContentCardExtras,
    productQuery,
  )

  const productIds = brazeContentCardExtras.product_id?.split(',') || []

  const idFacet: 'id' | 'objectID' = getIdFacetFromGranularity(
    brazeContentCardExtras.product_granularity || '',
  )
  const sortAlgoliaHitsFn: SortAlgoliaHitsFn = (productA, productB) => {
    const indexA =
      productIds.indexOf(productA[idFacet]) ?? Number.MAX_SAFE_INTEGER
    const indexB =
      productIds.indexOf(productB[idFacet]) ?? Number.MAX_SAFE_INTEGER

    return indexA - indexB
  }

  const refinedProductBlockProps: RefinedProductsBlockProps = {
    productLayout: 'slider',
    productQuery: finalProductQuery,
    clientOnly: true,
    blockPosition,
    tracking: {
      productRecoComponentPosition: index + 1,
      productComponent: 'PersonalizedFeed_RefinedProducts',
      personalizationContentCardFamily:
        brazeContentCardExtras.content_card_family || '',
    },
    // if there is no product_id, we don't need to sort the Algolia hits
    sortFn: productIds.length > 0 ? sortAlgoliaHitsFn : undefined,
    productGranularity: brazeContentCardExtras.product_granularity,
  }

  return {
    title: brazeContentCardExtras.title || '',
    tag: markRaw(RefinedProducts),
    props: refinedProductBlockProps,
  }
}

function buildRecommendationAlgoliaCarouselProps(
  brazeContentCardProps: BrazeContentCardExtras,
  logger: ReturnType<typeof useLogger>,
  { blockPosition, index }: { blockPosition: number; index: number },
) {
  if (!brazeContentCardProps.product_id) {
    logger.error(
      '[PERSONALIZED_CONTENT_CARD] Missing product_id for content card type: recommendations',
      {
        owners: ['bot-squad-content-front'],
      },
    )

    return undefined
  }
  if (
    !brazeContentCardProps.recommendation_type ||
    !['bought-together', 'related-products'].includes(
      brazeContentCardProps.recommendation_type,
    )
  ) {
    logger.error(
      '[PERSONALIZED_CONTENT_CARD] Unsupported recommendation_type for content card type: recommendations',
      {
        owners: ['bot-squad-content-front'],
      },
    )

    return undefined
  }

  const trackingData = {
    productRecoComponentPosition: index + 1,
    productComponent: 'PersonalizedFeed_RecommendationAlgoliaCarousel',
    personalizationContentCardFamily:
      brazeContentCardProps.content_card_family || '',
  }

  const isCrossSell =
    brazeContentCardProps.recommendation_type === 'bought-together'
  const isRelatedProducts =
    brazeContentCardProps.recommendation_type === 'related-products'

  const props: RecommendationAlgoliaCarouselProps = {
    cache: true,
    clientOnly: true,
    recommendationQuery: {
      objectIds: [brazeContentCardProps.product_id],
      model:
        brazeContentCardProps.recommendation_type as RecommendationAlgoliaCarouselProps['recommendationQuery']['model'],
      options: {
        queryParameters: {
          filters: brazeContentCardProps.filters,
        },
      },
    },
    blockPosition,
    trackingData: {
      ...trackingData,
      ...(isCrossSell && { zone: 'homepage_cross_sell_reco' }),
      ...(isRelatedProducts && {
        zone: 'homepage_reco',
        recoTab: brazeContentCardProps.content_card_family || undefined,
      }),
    },
    ...(isCrossSell && { blockZone: 'homepage_cross_sell_reco' }),
    ...(isRelatedProducts && { blockZone: 'homepage_reco' }),
    productGranularity: brazeContentCardProps.product_granularity,
  }

  return {
    title: brazeContentCardProps.title || '',
    tag: markRaw(RecommendationAlgoliaCarousel),
    props,
  }
}

function mapContentCard(
  card: BrazeContentCard,
  productQuery: PersonalizedFeedApiProps['productQuery'],
  { blockPosition, index }: { blockPosition: number; index: number },
): ContentCard | undefined {
  const { icon, type = '' } = card.extras

  const blockPropsResult = mapPropsByBlockType({
    type,
    brazeContentCardExtras: card.extras,
    productQuery,
    blockPosition,
    index,
  })

  if (!blockPropsResult) return undefined

  const parsedPriority = card.extras.priority
    ? Number.parseInt(card.extras.priority, 10)
    : undefined

  const priority =
    parsedPriority !== undefined && !Number.isNaN(parsedPriority)
      ? parsedPriority
      : undefined

  return {
    // The same content card with the same id can be received multiple times, so we need to generate a unique id for each occurrence
    id: crypto.randomUUID(),
    icon,
    title: blockPropsResult.title,
    tag: blockPropsResult.tag,
    props: blockPropsResult.props,
    productIds: card.extras.product_id?.split(',') || [],
    saleEndDate: card.extras.sale_end_date,
    priority,
    source: 'content-card',
  }
}

const dedupeCardsByCampaignId = (
  card: BrazeContentCard,
  index: number,
  arr: BrazeContentCard[],
): boolean => {
  // if the card has no campaign_id, we keep it
  if (!card.extras.campaign_id) {
    return true
  }

  // if the card has a campaign_id, we check if it is the first occurrence of the campaign_id
  return (
    index ===
    arr.findIndex((c) => c.extras.campaign_id === card.extras.campaign_id)
  )
}
export function transformContentCards({
  cards,
  feed,
  productQuery,
  blockPosition,
}: {
  cards: BrazeContentCard[]
  feed?: string
  productQuery: PersonalizedFeedApiProps['productQuery']
  blockPosition: number
}): ContentCard[] {
  return cards
    .filter(({ extras }) => extras.feed === feed)
    .filter(dedupeCardsByCampaignId)
    .map((card, index) =>
      mapContentCard(card, productQuery, { blockPosition, index }),
    )
    .filter((card) => !isEmpty(card))
}

export function transformCMSProductBlocks({
  items,
  blockPosition,
  title: blockTitle,
  source,
}: {
  items: PersonalizedFeedItem[]
  blockPosition: number
  title?: PersonalizedFeedApiProps['title']
  source: 'fallback' | 'additionalRecommendation'
}): ContentCard[] {
  return items.map((feedItem, index) => {
    const { saleEndDate, icon, priority, recommendation } = feedItem
    const {
      title,
      subtitle: _subtitle,
      ctaLink: _ctaLink,
      ...remainingProps
    } = recommendation.props

    return {
      id: feedItem.recommendation.id,
      tag: markRaw(BlockRenderer),
      title: title || blockTitle || '',
      props: {
        ...feedItem.recommendation,
        blockPosition,
        hydrationMode: 'eager',
        props: remainingProps,
        tracking: {
          productRecoComponentPosition: index + 1,
          productComponent: `PersonalizedFeed_${feedItem.recommendation.type}_${source}`,
          personalizationContentCardFamily: source,
        },
        withBottomMargin: false,
      },
      productIds: [],
      saleEndDate: saleEndDate ?? undefined,
      icon: icon?.icon,
      priority: priority ?? undefined,
      source,
    }
  })
}

export function mergeCardsByPriority(
  brazeCards: ContentCard[],
  additionalCards: ContentCard[],
): ContentCard[] {
  const taggedCards = [
    ...brazeCards.map((card) => ({ card, isBraze: true })),
    ...additionalCards.map((card) => ({ card, isBraze: false })),
  ]

  taggedCards.sort((a, b) => {
    const priorityA = a.card.priority ?? Infinity
    const priorityB = b.card.priority ?? Infinity

    if (priorityA !== priorityB) return priorityA - priorityB

    // if priorities are identical, put the Braze one first
    if (a.isBraze !== b.isBraze) return a.isBraze ? -1 : 1

    return 0
  })

  return taggedCards.map(({ card }) => card)
}

export function reindexTrackingPositions(cards: ContentCard[]): ContentCard[] {
  return cards.map((card, index) => {
    const { props } = card
    const position = index + 1

    // RecommendationAlgoliaCarousel uses `trackingData`
    if ('recommendationQuery' in props) {
      return {
        ...card,
        props: {
          ...props,
          trackingData: {
            ...(props.trackingData ?? {}),
            productRecoComponentPosition: position,
          },
        },
      }
    }

    // RefinedProducts use `tracking`
    if ('tracking' in props) {
      return {
        ...card,
        props: {
          ...props,
          tracking: {
            ...(props.tracking ?? {}),
            productRecoComponentPosition: position,
          },
        },
      }
    }

    return card
  })
}
